import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';

import OutputText from '@ey/canvascoreservices/OutputText';

import {labels} from '../../../../util/utils';
import {getCustomUserColumn} from './utils';

import BorderBox from '../../../Common/BorderBox';
import Button from '../../../CoreWrappers/Button';
import SelectDropdown from '../../../Common/SelectDropdown/SelectDropdown';
import StyledCustomColumnsContentChild from './StyledCustomColumnsContentChild';
import Hr from '../../../Common/Hr/HrAlt';
import Input from '../../../CoreWrappers/Input';
import Checkbox from '../../../CoreWrappers/Checkbox';

const CustomizeTableAside = ({userColumns, setUserColumns, isDisabled, editMode, changeMode}) => {
	const [columnName, setColumnName] = useState('');
	const [showColumnNameValidation, setShowColumnNameValidation] = useState(false);
	const [showSelectError, setShowSelectError] = useState(false);
	const [isVisibleForExternalsChecked, setIsVisibleForExternalsChecked] = useState(false);
	const defaultCustomAttribute = {value: '', hasError: false};
	const [columnAttributes, setColumnAttributes] = useState([]);
	const [minColumnAttributes, maxColumnAttributes] = [1, 5];
	useEffect(() => {
		clearCustomColumn();
	}, []);

	useEffect(() => {
		if (editMode !== null) {
			setColumnName(editMode.label);
			setIsVisibleForExternalsChecked(editMode.isVisibleForExternals);
			setColumnAttributes(normalizeToEditMode(editMode.attributes));
		}
	}, [editMode]);

	const normalizeToEditMode = (value) => {
		const customColumnsformated = value?.map((x) => {
			return {id: x.id, option: x.option, isVisibleForExternals: x.isVisibleForExternals, hasError: false};
		});
		return customColumnsformated;
	};

	//#region helpers
	const required = (text) => !_.trim(text ?? '').length;

	const evaluateCustomAttributes = () => {
		const isEmpty = !columnAttributes?.length;
		if (isEmpty) {
			setShowSelectError(isEmpty);
			return isEmpty;
		}

		const newAttributes = _.cloneDeep(columnAttributes);
		newAttributes.forEach((col) => (col.hasError = required(col.option)));
		setColumnAttributes(newAttributes);
		return newAttributes.some((attr) => attr.hasError);
	};

	const evaluateColumnName = () => {
		const isRequired = required(columnName);
		setShowColumnNameValidation(isRequired);
		return isRequired;
	};

	const isShowingErrors = () => {
		return evaluateColumnName() | evaluateCustomAttributes();
	};
	//#endregion helpers

	//#region setters
	const updateCustomAttribute = (idx, text, id) => {
		const newAttributes = _.cloneDeep(columnAttributes);
		newAttributes[idx].option = text;
		newAttributes[idx].hasError = false;
		setColumnAttributes(newAttributes);
	};

	const updateColumnName = (text) => {
		setColumnName(text);
		setShowColumnNameValidation(false);
	};

	const updateCustomAttributesQty = (qty) => {
		setShowSelectError(false);
		const currentQty = columnAttributes.length;
		if (qty < currentQty) {
			setColumnAttributes(columnAttributes.slice(0, qty));
		} else {
			const newList = _.cloneDeep(columnAttributes);
			for (let i = 0; i < qty - currentQty; i++) {
				newList.push({...defaultCustomAttribute});
			}
			setColumnAttributes(newList);
		}
	};
	//#endregion setters

	//#region render small components
	const renderHeader = (text) => <OutputText size="L">{text}</OutputText>;

	const renderText = (text) => (
		<OutputText body2 light>
			{text}
		</OutputText>
	);

	const renderVisibleToExternalContactsCheckbox = () => {
		const props = {
			id: 'VisibleToExternals',
			className: 'checkBox',
			styleName: 'checkoption-item',
			elementStyleName: 'ellipsis-one-line',
			ellipse: true,
			noOfLines: 1,
			label: labels.visibleToExternal,
			title: labels.visibleToExternal,
			checked: isVisibleForExternalsChecked,
			onChange: () => setIsVisibleForExternalsChecked(!isVisibleForExternalsChecked)
		};
		return <Checkbox {...props} />;
	};

	const renderTitleInput = () => (
		<Input
			id="columnName"
			disabled={isDisabled}
			label={labels.customColumnName}
			maxLength="50"
			required={true}
			errorMessage={showColumnNameValidation ? labels.required : null}
			value={columnName ?? ''}
			onChange={({target}) => updateColumnName(target.value)}
			clearButtonTitle={labels.clear}
		/>
	);

	//Custom values for custom column
	const renderCustomAttributes = () =>
		columnAttributes.map((attribute, idx) => {
			const id = `${labels.attribute} ${idx + 1}`;
			return (
				<Input
					id={id}
					disabled={isDisabled}
					label={id}
					maxLength="50"
					required={true}
					errorMessage={columnAttributes[idx].hasError ? labels.required : null}
					value={attribute.option ?? ''}
					onChange={({target}) => {
						updateCustomAttribute(idx, target.value, target?.id);
					}}
					clearButtonTitle={labels.clear}
				/>
			);
		});

	const renderDropdownNumberInput = (label, value, min, max, onChange) => {
		const options = [];
		for (let i = min; i <= max; i++) {
			options.push({value: `${i}`, label: `${i}`});
		}
		const handleOnChange = ({value}) => {
			onChange(+value);
		};

		const props = {
			id: label,
			name: label,
			formLabel: label,
			placeholder: label,
			value: value ? {value: `${value}`, label: `${value}`} : {},
			options: options,
			onChange: handleOnChange,
			isDisabled: isDisabled,
			isSearchable: false,
			errorMessage: labels.required,
			showErrorMessage: showSelectError,
			menuPlacement: 'bottom',
			formatOptionLabel: formatLabel
		};

		const formatLabel = (data) => renderText(data.label);

		return <SelectDropdown {...props} />;
	};
	//#endregion render small components

	const clearCustomColumn = () => {
		updateColumnName('');
		updateCustomAttributesQty(0);
		setIsVisibleForExternalsChecked(false);
		changeMode(null);
	};

	const addCustomColumn = (event) => {
		if (isShowingErrors()) {
			event.preventDefault;
			return;
		}
		columnAttributes.map((col) => (col.option = col.option.trim()));
		const newColumn = getCustomUserColumn(
			null,
			null,
			columnName.trim(),
			columnAttributes.map((col) => col),
			isVisibleForExternalsChecked,
			editMode !== null ? editMode.isChecked : true
		);
		if (editMode !== null) {
			newColumn.id = editMode.id;
			newColumn.apiId = editMode?.apiId;

			for (let index = 0; index < editMode.attributes; index++) {
				if (newColumn.attributes[index] !== null) {
					newColumn.attributes[index]['id'] = editMode?.attributes[index].id;
				}
			}
			const index = userColumns.findIndex((u) => u.id === editMode.id);
			if (index > -1) {
				userColumns.splice(index, 1);
			}
		}
		setUserColumns([...userColumns, newColumn]);
		clearCustomColumn();
	};

	return (
		<aside className="custom-columns-aside" id="aside">
			<BorderBox className="aside-box">
				<StyledCustomColumnsContentChild>
					<div className='header-text'>
						{renderHeader(editMode === null ? labels.addCustomColumn : labels.editCustomColum)}
						{renderText(labels.addCustomColumnLegend)}
					</div>
					{renderVisibleToExternalContactsCheckbox()}
					{renderTitleInput()}
					{renderDropdownNumberInput(
						labels.attributes1to5,
						columnAttributes.length,
						minColumnAttributes,
						maxColumnAttributes,
						updateCustomAttributesQty
					)}
					<Hr />
					{renderCustomAttributes()}
				</StyledCustomColumnsContentChild>
				<div className="box-footer">
					<Button
						disabled={isDisabled}
						label={editMode === null ? labels.add : labels.saveChanges}
						onClick={addCustomColumn}
						buttonTitle={editMode === null ? labels.add : labels.saveChanges}
						variant="secondary"
					/>
					<Button
						variant="ghost"
						disabled={isDisabled}
						label={labels.cancelLabel}
						onClick={clearCustomColumn}
						buttonTitle={labels.cancelLabel}
					/>
				</div>
			</BorderBox>
		</aside>
	);
};

CustomizeTableAside.defaultProps = {
	userColumns: [],
	setUserColumns: null,
	isDisabled: false
};

CustomizeTableAside.propTypes = {
	userColumns: PropTypes.arrayOf(PropTypes.any),
	setUserColumns: PropTypes.func,
	isDisabled: PropTypes.bool
};

export default CustomizeTableAside;
